import { Typography } from '../../../components/typography/typography';
import { Field } from '../../../components/field/field';
import { Button } from '../../../components/form/button';
import { useAdminFormState } from '../hooks/use-admin-form-state';
import { useAdminOperations } from '../hooks/use-admin-operations';
import { Admin, PasswordRequirements } from '../../../clients/quipli-service/admin/admin';
import { useEffect, useState } from 'react';

export interface PasswordChangeFormProps {
  // Admin data
  admin?: Admin;

  // UI customization
  showCurrentPassword?: boolean;
  submitText?: string;
  currentPasswordLabel?: string;
  newPasswordLabel?: string;
  confirmPasswordLabel?: string;

  // Callbacks
  onSuccess?: () => void;
}

export const PasswordChangeForm = ({
  // Admin data
  admin,

  // UI customization
  showCurrentPassword = false,

  // Callbacks
  onSuccess,
}: PasswordChangeFormProps) => {
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirements | null>(null);

  const { updateUser, updateLoggedProfile, isUpdating, fetchPasswordRequirements } = useAdminOperations();

  const {
    currentPassword,
    newPassword,
    confirmNewPassword,
    errorMessage,
    isDisabled,
    setCurrentPassword,
    setNewPassword,
    setConfirmNewPassword,
  } = useAdminFormState({
    admin,
    type: showCurrentPassword ? 'changeSelfPassword' : 'changePassword',
    passwordRequirements: passwordRequirements || undefined,
  });

  useEffect(() => {
    fetchPasswordRequirements().then((requirements) => {
      setPasswordRequirements(requirements);
    });
  }, []);

  const handleSubmit = async () => {
    if (!admin) return;

    let updatedAdmin: Admin | null = null;

    if (showCurrentPassword) {
      updatedAdmin = await updateLoggedProfile({
        adminUsername: admin.adminUsername,
        adminEmail: admin.adminEmail,
        adminFullName: admin.adminFullName,
        currentPassword,
        adminPassword: newPassword,
      });
    } else {
      updatedAdmin = await updateUser(admin.adminId, {
        adminUsername: admin.adminUsername,
        adminEmail: admin.adminEmail,
        adminFullName: admin.adminFullName,
        adminPassword: newPassword,
      });
    }

    if (updatedAdmin) {
      onSuccess?.();
    }
  };
  return (
    <div className="w-full flex flex-column gap-2">
      {showCurrentPassword && (
        <Field
          data-password="check-password"
          dataCy="current-password"
          className="w-full"
          type="password"
          label="Current Password"
          value={currentPassword}
          onChange={setCurrentPassword}
          isRequired={true}
        />
      )}

      <Field
        data-password="check-password"
        dataCy="new-password"
        className="w-full"
        type="password"
        label="New Password"
        value={newPassword}
        onChange={setNewPassword}
        isRequired={true}
      />

      <Field
        data-password="check-password"
        dataCy="confirm-password"
        className="w-full"
        type="password"
        label="Confirm Password"
        value={confirmNewPassword}
        onChange={setConfirmNewPassword}
        isRequired={true}
      />

      {errorMessage && <div className="text-[var(--error-500)]">{errorMessage}</div>}

      <div className="dialog-actions !px-0 !pb-0">
        <div className="flex flex-col sm:flex-row flex-end">
          <Button
            className="button button-yellow button-primary mb-2"
            onClick={handleSubmit}
            disabled={isDisabled}
            dataCy="save-changes"
          >
            <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
              {isUpdating ? 'Saving...' : 'Save changes'}
            </Typography>
          </Button>
        </div>
      </div>
    </div>
  );
};
